import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSettings, assetUrl } from "../settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 rounded-none border-white/20 bg-transparent text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-turquoise";

const FIELDS = [
  { key: "company_name", label: "Company name" },
  { key: "tagline", label: "Tagline (shown under the name)" },
  { key: "hero_eyebrow", label: "Hero eyebrow (small line above the headline)" },
  { key: "hero_line_1", label: "Headline line 1" },
  { key: "hero_line_2", label: "Headline line 2" },
  { key: "hero_line_3", label: "Headline line 3 (shown in green)" },
  { key: "phone", label: "Phone number" },
  { key: "email", label: "Contact email" },
  { key: "address", label: "Office address" },
  { key: "hours", label: "Business hours" },
  { key: "facebook_url", label: "Facebook page URL" },
  { key: "instagram_url", label: "Instagram page URL" },
];

export const EditWebsite = () => {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => setForm(settings), [settings]);

  const logoSrc = form.logo_url ? assetUrl(form.logo_url) : "/assets/logo.png";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      [...FIELDS.map((f) => f.key), "hero_subtext", "marquee_items"].forEach((k) => {
        payload[k] = form[k] ?? "";
      });
      await axios.put(`${API}/settings`, payload, { withCredentials: true });
      toast.success("Website content updated.");
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) {
      toast.error("Choose a logo image first.");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      await axios.post(`${API}/settings/logo`, fd, { withCredentials: true });
      toast.success("Logo updated across the site.");
      setLogoFile(null);
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Logo upload failed.");
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="edit-website-panel">
      <div className="border border-white/10 bg-surface/60 p-6 sm:p-8">
        <p className="font-heading text-sm font-bold uppercase tracking-[0.2em]">Company logo</p>
        <p className="mt-1 text-xs text-white/40">Upload your real logo here — it replaces the logo everywhere on the site.</p>
        <div className="mt-5 flex flex-wrap items-center gap-6">
          <img
            src={logoSrc}
            alt="Current logo"
            data-testid="admin-logo-preview"
            className="h-20 w-20 rounded-full border border-white/15 object-cover"
          />
          <div className="min-w-[220px] flex-1">
            <Input
              data-testid="admin-logo-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className={`${inputCls} cursor-pointer py-2.5 file:mr-3 file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-xs file:text-white`}
            />
          </div>
          <button
            data-testid="admin-logo-upload-button"
            onClick={uploadLogo}
            disabled={uploadingLogo}
            className="flex items-center gap-2 bg-volt px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-white disabled:opacity-50"
          >
            <Upload className="h-4 w-4" /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-surface/60 p-6 sm:p-8">
        <p className="font-heading text-sm font-bold uppercase tracking-[0.2em]">Website text &amp; contact info</p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-white/40">{f.label}</span>
              <Input
                data-testid={`site-field-${f.key}`}
                value={form[f.key] ?? ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className={inputCls}
              />
            </label>
          ))}
        </div>
        <label className="mt-4 block">
          <span className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-white/40">Hero intro text</span>
          <Textarea
            data-testid="site-field-hero-subtext"
            value={form.hero_subtext ?? ""}
            onChange={(e) => setForm({ ...form, hero_subtext: e.target.value })}
            className={`${inputCls} h-24 resize-none py-3`}
          />
        </label>
        <label className="mt-4 block">
          <span className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-white/40">
            Scrolling marquee items (comma separated)
          </span>
          <Textarea
            data-testid="site-field-marquee-items"
            value={form.marquee_items ?? ""}
            onChange={(e) => setForm({ ...form, marquee_items: e.target.value })}
            className={`${inputCls} h-20 resize-none py-3`}
          />
        </label>
        <button
          data-testid="admin-site-save-button"
          onClick={save}
          disabled={saving}
          className="mt-6 flex items-center gap-2 bg-volt px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-white disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};
